import { useEffect } from 'react';
import { OrthographicCamera, PerspectiveCamera, Vector3 } from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

export function useOrbitControlsCursorFocusPoint(
  controls?: OrbitControls,
  cursorPosition?: Vector3,
  camera?: PerspectiveCamera | OrthographicCamera,
  ref?: React.RefObject<HTMLElement>,
) {
  useEffect(() => {
    if (!(camera instanceof PerspectiveCamera) || !controls || !cursorPosition || !ref?.current) {
      return;
    }

    const forward = new Vector3();
    const tmp = new Vector3();

    const EPS = 0.05; // prevents target going too close / behind camera

    const onWheel = () => {
      // Camera forward direction (world space)
      camera.getWorldDirection(forward).normalize();

      // Distance along forward axis to the hit (projection)
      const d = tmp.subVectors(cursorPosition, camera.position).dot(forward);

      // If hit is behind camera or extremely close, ignore
      if (d <= EPS) {
        return;
      }

      // New target is straight ahead, same view direction, just new depth
      const newTarget = tmp.copy(camera.position).addScaledVector(forward, d);

      // Update target (collinear => no "jump" in view direction)
      controls.target.copy(newTarget);

      // Let OrbitControls do the actual zoom for this wheel event
      // (important so internal state stays consistent)
      controls.update();
    };

    const refElement = ref.current;
    refElement.addEventListener('wheel', onWheel);
    return () => refElement?.removeEventListener('wheel', onWheel);
  }, [camera, cursorPosition, controls, ref]);
}
